import React, { useEffect, useState, useMemo } from 'react';
import { Container, Loader, Table, Alert, Input, Button, Modal } from '@itds/elements/index.js';
import EditConfigField from './EditConfigField';
import AddConfigField from './AddConfigField';
import H1 from '@brokerage/components/H1';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConfigFields,
  setCurrentPage,
  setSearchTerm,
  clearCache
} from '@redux/brokerageSlices/configSlice';
import { Api } from '@brokerage/utils/Api';

const ConfigFieldsShow = () => {
  const dispatch = useDispatch();
  const { configFields, pagination, loading, error, currentPage, searchTerm } = useSelector(
    state => state.configFields
  );

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [field, setField] = useState(null);
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [creatingDefaults, setCreatingDefaults] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [createStatus, setCreateStatus] = useState({ created: 0, skipped: 0, failed: 0 });
  const [notification, setNotification] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchConfigFields({ page: currentPage, search_term: searchTerm }));
  }, [dispatch, currentPage, searchTerm]);

  const handleSave = () => {
    setField(null);
    dispatch(fetchConfigFields({ page: currentPage, search_term: searchTerm }));
  };

  const handleAddSave = field => {
    setShowAddDrawer(false);

    if (field) {
      setNotification({
        type: 'success',
        message: 'Config Field added successfully',
        details: `The field "${field.name}" has been created.`
      });
    } else {
      // Generic notification if field isn't passed
      setNotification({
        type: 'success',
        message: 'Config Field added successfully',
        details: 'The new config field has been created.'
      });
    }

    setTimeout(() => {
      // Clear the search term to refresh the grid
      setLocalSearchTerm('');
      dispatch(setSearchTerm(''));

      // Refresh the list with empty search term
      dispatch(fetchConfigFields({ page: currentPage, search_term: '' }));
    }, 3000);
  };

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchTerm(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);

    setLocalSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchTerm(localSearchTerm));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  const handlePageChange = newPage => {
    dispatch(setCurrentPage(newPage));
    dispatch(fetchConfigFields({ page: newPage, search_term: searchTerm }));
  };

  // Helper function to process a single build
  const processBuild = async (build, field, counters) => {
    if (!build || !build.guid) {
      return { ...counters, failed: counters.failed + 1 };
    }

    try {
      // Check if config value already exists
      const valuesResponse = await Api.loadConfigValues(build.guid);

      if (!valuesResponse || !valuesResponse.data) {
        return { ...counters, failed: counters.failed + 1 };
      }

      const existingValues = valuesResponse.data;
      const hasValue = existingValues.some(value => value.config_field_guid === field.guid);

      if (hasValue) {
        return { ...counters, skipped: counters.skipped + 1 };
      }

      // Create default value based on field type
      const defaultValue = {
        config_field_guid: field.guid, // ConfigFieldGuid
        build_guid: build.guid, // BuildGuid
        mobile_app_guid: build.mobile_app_guid // MobileAppGuid
      };

      // Set appropriate default value based on field type
      // Using property names that match the backend model
      switch (field.field_type) {
        case 'text':
          defaultValue.text_value = field.text_default || '';
          break;
        case 'textarea':
          defaultValue.textarea_value = field.textarea_default || '';
          break;
        case 'integer':
          defaultValue.integer_value =
            field.integer_default !== null && field.integer_default !== ''
              ? Number(field.integer_default)
              : null;
          break;
        case 'decimal':
          defaultValue.decimal_value =
            field.decimal_default !== null && field.decimal_default !== ''
              ? Number(field.decimal_default)
              : null;
          break;
        case 'boolean':
          defaultValue.boolean_value = field.boolean_default || false;
          break;
        case 'array':
          defaultValue.array_values = field.array_defaults || [];
          break;
        case 'file':
          // We have disabled files so this will not be hit
          if (field.file_default) {
            if (field.file_default !== null) {
              defaultValue.file_value = field.file_default;
            }
          } else {
            defaultValue.file_value = null;
          }
          break;
        default:
          break;
      }

      // Create the config value
      await Api.createConfigValue(build.guid, defaultValue);
      return { ...counters, created: counters.created + 1 };
    } catch (error) {
      return { ...counters, failed: counters.failed + 1 };
    }
  };

  // Function to create missing config values for a specific field
  const createMissingConfigValues = async field => {
    try {
      setSelectedField(field);
      setCreatingDefaults(true);
      setCreateStatus({ created: 0, skipped: 0, failed: 0 });
      setNotification(null);

      let counters = { created: 0, skipped: 0, failed: 0 };

      // Process each version of the field
      for (const version of field.versions) {
        try {
          // Load mobile app builds for this version
          const buildsResponse = await Api.loadMobileAppBuilds({ github_release_version: version });

          // Only process if we have valid data
          if (buildsResponse && buildsResponse.data && Array.isArray(buildsResponse.data)) {
            const builds = buildsResponse.data;

            // Process each build
            for (const build of builds) {
              counters = await processBuild(build, field, counters);
              // Update status as we go
              setCreateStatus(counters);
            }
          }
        } catch (error) {
          counters = { ...counters, failed: counters.failed + 1 };
          setCreateStatus(counters);
        }
      }

      setNotification({
        type: 'success',
        message: `Operation completed for field: ${field.name}`,
        details: `Created: ${counters.created}, Skipped: ${counters.skipped}, Failed: ${counters.failed}`
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error creating missing config values',
        details: error.message
      });
    } finally {
      setCreatingDefaults(false);
      setSelectedField(null);
    }
  };

  // Function to get the field type of a config value
  const getValueType = value => {
    if (value.text_value !== null) return 'text';
    if (value.textarea_value !== null) return 'textarea';
    if (value.integer_value !== null) return 'integer';
    if (value.decimal_value !== null) return 'decimal';
    if (value.boolean_value !== null) return 'boolean';
    if (value.array_values && Array.isArray(value.array_values)) return 'array';
    if (value.file_value !== null) return 'file';
    return 'unknown';
  };

  // Function to initiate the delete process
  const handleDeleteConfigField = field => {
    setFieldToDelete(field);
    setShowDeleteConfirm(true);
  };

  // Function to confirm and execute the delete operation
  const confirmDeleteConfigField = async () => {
    if (!fieldToDelete) return;

    try {
      setIsDeleting(true);
      setShowDeleteConfirm(false);

      // Get all builds to find all config values for this field
      let allValues = [];
      const buildsResponse = await Api.loadMobileAppBuilds({});

      if (buildsResponse && buildsResponse.data && Array.isArray(buildsResponse.data)) {
        const builds = buildsResponse.data;

        // For each build, get config values and find those associated with this field
        for (const build of builds) {
          if (build && build.guid) {
            const valuesResponse = await Api.loadConfigValues(build.guid);

            if (valuesResponse && valuesResponse.data) {
              const fieldValues = valuesResponse.data.filter(
                value => value.config_field_guid === fieldToDelete.guid
              );

              allValues = [...allValues, ...fieldValues];
            }
          }
        }
      }

      const valueTypes = {};

      allValues.forEach(value => {
        const type = getValueType(value);

        valueTypes[type] = (valueTypes[type] || 0) + 1;
      });

      // Delete all config values associated with this field
      for (const value of allValues) {
        await Api.deleteConfigValue(value.guid);
      }

      // delete the config field itself
      await Api.deleteConfigField(fieldToDelete.guid);

      let detailsMessage = `The field "${fieldToDelete.name}" and ${allValues.length} associated config values have been deleted.`;

      // Add type breakdown if there are values
      if (allValues.length > 0) {
        const typeBreakdown = Object.entries(valueTypes)
          .map(([type, count]) => `${count} ${type}`)
          .join(', ');

        detailsMessage += `\nValues by type: ${typeBreakdown}`;
      }

      setNotification({
        type: 'success',
        message: 'Field deleted successfully',
        details: detailsMessage
      });

      // Clear the search term to refresh the grid
      setLocalSearchTerm('');
      dispatch(setSearchTerm(''));

      // Refresh the list with empty search term
      dispatch(fetchConfigFields({ page: currentPage, search_term: '' }));
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Error deleting field',
        details: error.message
      });
    } finally {
      setIsDeleting(false);
      setFieldToDelete(null);
    }
  };

  // Cancel delete operation
  const cancelDeleteConfigField = () => {
    setShowDeleteConfirm(false);
    setFieldToDelete(null);
  };

  // Render different content based on loading and creatingDefaults states
  const renderContent = () => {
    if (loading || isDeleting) {
      return (
        <div>
          <Loader />
          {isDeleting && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <p>Deleting field and associated config values...</p>
            </div>
          )}
        </div>
      );
    }

    if (creatingDefaults) {
      return (
        <div>
          <Loader />
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <p>Creating missing values for field: {selectedField?.name}</p>
            <p>
              Created: {createStatus.created}, Skipped: {createStatus.skipped}, Failed:{' '}
              {createStatus.failed}
            </p>
          </div>
        </div>
      );
    }

    return (
      <Table
        apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
        columns={[
          { id: 'name', label: 'Name', width: '20%' },
          { id: 'group', label: 'Group', width: 120 },
          { id: 'label', label: 'Label', width: '20%' },
          { id: 'field_type', label: 'Type', width: 80 },
          {
            id: 'versions',
            label: 'Github Versions',
            render: row => <>{row.versions.toString().split(',').join(', \n')}</>
          },
          {
            id: 'actions',
            label: 'Actions',
            width: 240,
            render: row => (
              <div style={{ display: 'flex', gap: '5px' }}>
                <Button
                  disabled={creatingDefaults || isDeleting}
                  onClick={e => {
                    e.stopPropagation();
                    createMissingConfigValues(row);
                  }}
                  small={true}
                >
                  Init Config Values Versions
                </Button>
                <Button
                  disabled={creatingDefaults || isDeleting}
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteConfigField(row);
                  }}
                  small={true}
                  type='danger'
                >
                  Delete
                </Button>
              </div>
            )
          }
        ]}
        data={configFields}
        onPageChange={handlePageChange}
        onRowClick={row_object => setField(row_object)}
      />
    );
  };

  return (
    <Container>
      <EditConfigField
        field={field}
        onClose={() => setField(null)}
        onSave={handleSave}
        show={!!field}
      />

      <AddConfigField
        onClose={() => setShowAddDrawer(false)}
        onSave={handleAddSave}
        show={showAddDrawer}
      />

      <H1>Config Fields</H1>

      {notification && (
        <Alert
          onClose={() => setNotification(null)}
          style={{ marginBottom: '15px' }}
          type={notification.type}
        >
          <div>
            <strong>{notification.message}</strong>
            <div style={{ whiteSpace: 'pre-line' }}>{notification.details}</div>
          </div>
        </Alert>
      )}

      <div style={{ marginBottom: 20 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search config fields...'
          style={{ width: 300 }}
          value={localSearchTerm}
        />
      </div>

      <div
        style={{
          marginBottom: 30,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <Button onClick={() => setShowAddDrawer(true)} style={{ width: 'auto' }}>
          Add New Config Field
        </Button>
      </div>

      {error && <Alert type='error'>{error}</Alert>}

      {renderContent()}

      {/* Confirmation Modal */}
      {showDeleteConfirm && fieldToDelete && (
        <Modal
          isOpen={showDeleteConfirm}
          onClose={cancelDeleteConfigField}
          size='md'
          title='Confirm Deletion'
        >
          <div
            style={{
              padding: '20px 10px',
              fontFamily: 'inherit' // Use the same font as the rest of the app
            }}
          >
            <p
              style={{
                fontSize: '14px',
                lineHeight: '1.5',
                marginBottom: '20px'
              }}
            >
              Are you sure you want to delete the field "<strong>{fieldToDelete.name}</strong>" and
              all its associated config values?
              <br />
              <br />
              This action cannot be undone.
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '15px',
                marginTop: '30px'
              }}
            >
              <Button
                onClick={cancelDeleteConfigField}
                style={{ minWidth: '100px', padding: '8px 16px' }}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteConfigField}
                style={{ minWidth: '100px', padding: '8px 16px' }}
                type='danger'
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Container>
  );
};

export default ConfigFieldsShow;
